import moment from "moment";

// Helper function to extract standard code from description format
// e.g., "Responsible Wool Standard (RWS)" -> "RWS"
function extractStandardCode(standardDescription: string): string {
  const match = standardDescription.match(/\(([A-Z]+)\)$/);
  return match ? match[1] : standardDescription;
}

export interface ScopeCertificateDetails {
  scopeCertificateNo: string; // Numeric field
  brandName: string[]; // Multi-select dropdown
  gotsSCNo: string; // Optional text field
  scLegacyNo: string; // Single text value in v4 (API may return array — normalised to string in mapper)
  scCBAuditorId: string[]; // Dropdown
  scNumberOfFarms: number | null; // Number of farms - moved from StandardDetails
  isFarmSC: string; // "Y" or "N" — Is this SC for a Farm? (radio field)
  isFarmingAreaOnly?: string; // "Y" or "N" — Does it involve Farming (area) only? (UI-only radio field)
  scCBLicensingCode: string; // Text field
  scVersionNo: string; // Numeric field
  scProductLimitations: string; // New field from API
  scCreationTimestamp: string; // New field from API
  scCbLicensingCodeDescription: string; // New field from API
  ccsFlag: string; // Does CCS Apply field (radio - no description needed)
  stdClaimsPolicy:string;
  certifiedOrganizationApprovedClaims: string; // Required field for API
  submittedFromUi?: string | boolean;
}

export interface StandardDetails {
  scStandard: string; // Text field
  scStandardDescription?: string;
  scStandardVersion: string; // Text field
  scStandardAddedDate: string; // Date field - API field name
  scStandardRemovedDate: string; // Date field - API field name
  scStandardWithdrawalReason: string[]; // Multi-select dropdown field - API field name
  scStandardWithdrawalReasonDescription: string; // Description for scStandardWithdrawalReason dropdown
  scStandardReAdded?: boolean; // True when addedDate > removedDate and removedDate is not empty
  // Removed: scNumberOfFarms - moved to ScopeCertificateDetails
  // Removed: numberOfAuditDays
}

export interface ScopeCertificateDates {
  scStatus: string; // Dropdown
  scStatusDescription: string; // Description for scStatus dropdown
  scSuspensionReason: string; // Dropdown
  scSuspensionReasonDescription: string; // Description for scSuspensionReason dropdown
  scCbAccreditationNo: string;
  scCreatedDate: string; // Date in 'YYYY-MM-DD' format (required)
  scLastUpdatedDate: string; // Date in 'YYYY-MM-DD' format (required) - Updated to match API field name
  scExtendedUntilDate: string; // Date in 'YYYY-MM-DD' format (optional) - Updated to match API field name
  scLastAuditStartDate: string; // Date in 'YYYY-MM-DD' format (optional) - Updated to match API field name
  scValidFromDate: string; // Date in 'YYYY-MM-DD' format (required) - Updated to match API field name
  scValidUntilDate: string; // Date in 'YYYY-MM-DD' format (required) - Updated to match API field name
  scWithdrawalDate: string; // Date in 'YYYY-MM-DD' format (optional)
  scLastAuditDate: string; // Date in 'YYYY-MM-DD' format (optional)
  scStateOrProvince: string; // Dropdown (required)
  scStateOrProvinceDescription: string; // Description for scStateOrProvince dropdown
  scPlaceOfIssue: string; // StateName (CountryCode-StateCode) for API (required)
  scPlaceOfIssueDescription: string; // StateName (CountryCode-StateCode) for API (required)
  scCbAccreditationBody: string; // Dropdown (required) - API field name
  scCbAccreditationBodyDescription: string; // Description for scCbAccreditationBody dropdown
  scCbScopeStipulations: string; // Text field (optional) - API field name
}

export interface AccreditationDetails {
  scScopeStipulations: string; // Optional text field
  accreditationBody: string; // Dropdown
  accreditationBodyDescription: string; // Description for accreditationBody dropdown
  accreditationNo: string; // Optional text field
  inspectionBody: string; // Optional text field
}

export interface NonMandatoryCriterionDetails {
  scNonMandatoryCriterionStandard: string; // Dropdown field (required)
  scNonMandatoryCriterionStandardDescription: string; // Description for scNonMandatoryCriterionStandard dropdown
  scNonMandatoryCriterionVersion: string; // Text field (required)
  scNonMandatoryCriterionNumber: string; // Text field (required)
  scNonMandatoryCriterionStatus: string; // Dropdown field (required)
  scNonMandatoryCriterionStatusDescription: string; // Description for scNonMandatoryCriterionStatus dropdown
  scNonMandatoryCriterionDescription?: string; // Derived Description field
  scNonMandatoryCriterionType?: string; // Derived Type field
}

export interface ScopeCertificateForm {
  scopeCertificateDetails: ScopeCertificateDetails;
  standardDetails: StandardDetails[];
  scopeCertificateDates: ScopeCertificateDates;
  scNonMandatoryCriterionInformation: NonMandatoryCriterionDetails[];
  // Removed: accreditationDetails
}

export function mapToScopeCertificateForm(response: any): ScopeCertificateForm {
  // Parse place of issue to extract state code
  const placeOfIssue = response?.scPlaceOfIssue || "";
  
  const {
    scNo = '',
    scVersionNo = '',
    scPlaceOfIssueDescription = ''
  } = response || {};

  let stateCode = "";
  let stateName = scPlaceOfIssueDescription || "";

  // Normalize scPlaceOfIssue to canonical "IN-TN" code regardless of API format.
  // Known formats:
  //   A) "IN-TN"                      — already canonical, use as-is
  //   B) "India(IN),Tamil Nadu(TN)"   — API format, comma no space, short code
  //   C) "Country (CC), State (CC-SC)"— old legacy with full code
  //   D) "State (CC-SC)"              — state only
  // ISO 3166-2 codes can be letter-only ("IN-TN") OR alphanumeric with space/hyphen ("MN 051").
  // Treat any value without a comma or bare-name-with-parentheses as an already-resolved code.
  const isAlreadyCode = placeOfIssue &&
    !placeOfIssue.includes(',') &&
    !placeOfIssue.match(/^[A-Za-z\s]+\([^)]+\)$/);

  if (placeOfIssue) {
    if (isAlreadyCode) {
      // Already a clean code ("IN-TN", "MN 051", "AU-VIC", etc.) — use as-is
      stateCode = placeOfIssue;
    } else if (placeOfIssue.includes(',')) {
      // Format B or C — has a comma
      const parts = placeOfIssue.split(',');
      const countryPart  = parts[0].trim();
      const rawStatePart = parts.slice(1).join(',').trim();
      const ccMatch = countryPart.match(/\(([^)]+)\)$/);
      const countryCode = ccMatch ? ccMatch[1].trim() : '';
      const sm = rawStatePart.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
      if (sm) {
        if (!stateName) stateName = sm[1].trim();
        const rawCode = sm[2].trim();
        // Only prefix countryCode when it is a real 2-3 uppercase ISO code (e.g. "IN", "AU").
        // Prevents descriptive words like "South" in "Korea (South)" from producing "South-KR".
        const isValidISOCountryCode = /^[A-Z]{2,3}$/.test(countryCode);
        stateCode = (rawCode.includes('-') || rawCode.includes(' '))
                  ? rawCode
                  : (isValidISOCountryCode && countryCode) ? `${countryCode}-${rawCode}` : rawCode;
      }
    } else {
      // Format D — "State (CC-SC)" no comma
      const sm = placeOfIssue.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
      if (sm) {
        if (!stateName) stateName = sm[1].trim();
        stateCode = sm[2].trim();
      }
    }
  }

  // Display string for the SearchInput: "Tamil Nadu (IN-TN)" or "Sühbaatar (MN 051)"
  const stateDisplay = stateCode
    ? `${stateName || stateCode} (${stateCode})`
    : stateName || "";

  let scStateOrProvince_initial = "";
  // isAlreadyCode covers all canonical formats (letter-only and alphanumeric)
  if (isAlreadyCode) {
    // Extract just the subdivision part after the first separator (hyphen or space)
    const sepMatch = stateCode.match(/^[A-Z0-9]{1,3}[-\s](.+)$/);
    const statePart = sepMatch ? sepMatch[1] : stateCode;
    scStateOrProvince_initial = scPlaceOfIssueDescription
      ? `${scPlaceOfIssueDescription} (${statePart})`
      : stateDisplay;
  } else {
    scStateOrProvince_initial = stateDisplay;
  }

  return {
    scopeCertificateDetails: {
      scopeCertificateNo: scNo?scNo.substring(scNo.indexOf('-') + 1) : "", // Converting scId to number
      brandName: response.brandName || [], // Wrapping brandName in an array
      gotsSCNo: response.gotsScNo || "", // Optional field
      // scLegacyNo is a plain text field in v4 (single value). The API may return
      // a string ("na"), an array (["na"]), or a comma-separated string ("na, nb").
      // Normalise to a single string so Yup .string() validation doesn't crash.
      scLegacyNo: Array.isArray(response.scLegacyNo)
        ? response.scLegacyNo.join(', ')
        : (typeof response.scLegacyNo === 'string' ? response.scLegacyNo : ''),
      scCBAuditorId: response.scCbAuditorId || [], // Taking the first auditor ID
      scNumberOfFarms: response.scNumberOfFarms || null, // Moved from StandardDetails
  isFarmSC: response.isFarmSC || "", // "Y" or "N" — restored from API on back-navigation
      isFarmingAreaOnly: (() => {
        if (response.isFarmSC === "Y") {
          if (response.scNumberOfFarms !== undefined && response.scNumberOfFarms !== null && Number(response.scNumberOfFarms) > 0) {
            return "N";
          }
          return "Y";
        }
        return "";
      })(),
      scCBLicensingCode: response.scCbLicensingCode || "",
      scVersionNo: scVersionNo?scVersionNo.substring(scVersionNo.indexOf('-') + 1): "",
      scProductLimitations: response.scProductLimitations || "", // New field from API
      scCreationTimestamp: response.scCreationTimestamp || "", // New field from API
      scCbLicensingCodeDescription: response.scCbLicensingCodeDescription || "", // New field from API
      ccsFlag: response.ccsFlag || "", // Does CCS Apply field
      stdClaimsPolicy:response?.stdClaimsPolicy||"",
      // Normalize to uppercase so lowercase API values (e.g. "both", "none") match
      // the dropdown option values ("BOTH", "NONE", "PC", "CC").
      certifiedOrganizationApprovedClaims: (response.certifiedOrganizationApprovedClaims || "").toUpperCase() || "",
      submittedFromUi: response.submittedFromUi !== undefined ? response.submittedFromUi : "",
    },
    standardDetails: response.scStandardInformation?.length > 0 ? response.scStandardInformation.map((item: any) => ({
      scStandard: item.scStandard,
      scStandardDescription: item.scStandardDescription,
      scStandardVersion: item.scStandardVersion,
      scStandardAddedDate: item.scStandardAddedDate,  // API field name
      scStandardRemovedDate: item.scStandardRemovedDate,  // API field name
      scStandardWithdrawalReason: Array.isArray(item.scStandardWithdrawalReason) 
        ? item.scStandardWithdrawalReason.map((reason: string) => {
            // Parse "code; description" format back to just the description for form display
            const parts = reason.split('; ');
            return parts.length > 1 ? parts[1] : reason;
          })
        : [],  // API field name - convert from ["code1; description1", "code2; description2"] to ["description1", "description2"]
      scStandardWithdrawalReasonDescription: item.scStandardWithdrawalReasonDescription || "", // Description for scStandardWithdrawalReason dropdown
      scStandardReAdded: item.scStandardReAdded ?? false, // Re-added flag
    })) : [],
    scopeCertificateDates: {
      scStatus: response.scStatus || "", // Dropdown value
      scStatusDescription: response.scStatusDescription || "", // Description for scStatus dropdown
      scSuspensionReason: response.scSuspensionReason || "", // Dropdown value
      scSuspensionReasonDescription: response.scSuspensionReasonDescription || "", // Description for scSuspensionReason dropdown
      scCbAccreditationNo: response.scCbAccreditationNo || "",
      scCreatedDate: response.scCreatedDate || "",
      scLastUpdatedDate: response.scLastUpdatedDate || "", // Updated to match API field name
      scExtendedUntilDate: response.scExtendedUntilDate || "", // Updated to match API field name
      scLastAuditStartDate: response.scLastAuditStartDate || "", // Updated to match API field name
      scValidFromDate: response.scValidFromDate || "", // Updated to match API field name
      scValidUntilDate: response.scValidUntilDate || "", // Updated to match API field name
      scWithdrawalDate: response.scWithdrawalDate || "",
      scLastAuditDate: response.scLastAuditDate || "",
      scStateOrProvince: scStateOrProvince_initial,
      scStateOrProvinceDescription: response.scStateOrProvinceDescription || stateName || "",
      // ★ Always store the canonical code (e.g. "IN-TN") — never the raw API string.
      //   scopeCertificateFormToMap reads this directly when building the POST payload.
      scPlaceOfIssue: stateCode || response.scPlaceOfIssue || "",
      scPlaceOfIssueDescription: response.scPlaceOfIssueDescription || stateName || "",
      scCbAccreditationBody: response.scCbAccreditationBody || "",
      scCbAccreditationBodyDescription: response.scCbAccreditationBodyDescription || "", // Description for scCbAccreditationBody dropdown
      scCbScopeStipulations: response.scCbScopeStipulations || "",
    },
    scNonMandatoryCriterionInformation: response.scNonMandatoryCriterionInformation?.length > 0 ? response.scNonMandatoryCriterionInformation.map((item: any) => ({
      scNonMandatoryCriterionStandard: item.scNonMandatoryCriterionStandard || "",
      scNonMandatoryCriterionStandardDescription: item.scNonMandatoryCriterionStandardDescription || "", // Description for scNonMandatoryCriterionStandard dropdown
      scNonMandatoryCriterionVersion: item.scNonMandatoryCriterionVersion || "",
      scNonMandatoryCriterionNumber: item.scNonMandatoryCriterionNumber || "",
      scNonMandatoryCriterionStatus: item.scNonMandatoryCriterionStatus || "",
      scNonMandatoryCriterionStatusDescription: item.scNonMandatoryCriterionStatusDescription || "", // Description for scNonMandatoryCriterionStatus dropdown
      scNonMandatoryCriterionDescription: item.scNonMandatoryCriterionDescription || "",
      scNonMandatoryCriterionType: item.scNonMandatoryCriterionType || "",
    })) : [],
  };
}

export function scopeCertificateFormToMap(scopeCertificateForm: ScopeCertificateForm, SC_ID?: string | undefined, cbLicenseCode?: any): any {
  const shortCode = cbLicenseCode?.replace(/^CB-/, "") || "";
  const { scopeCertificateDetails, standardDetails, scopeCertificateDates, scNonMandatoryCriterionInformation } = scopeCertificateForm;
  
  // Use the formatted place of issue directly
  const placeOfIssue = scopeCertificateDates.scPlaceOfIssue || "";
  const placeOfIssueDescription = scopeCertificateDates.scPlaceOfIssueDescription || "";
  
  const statusOptions: any = [
    { label: "Valid", value: "V" }, // Represents "Valid"
    { label: "Expired", value: "E" }, // Represents "Expired"
    { label: "Suspended", value: "S" }, // Represents "Suspended"
    { label: "Withdrawn", value: "W" }, // Represents "Withdrawn"
  ]
  const scStandardOptions: any = [
    { label: "Organic Content Standard", value: "OCS" }, // Represents "Valid"
    { label: "Global Recycled Standard", value: "GRS" }, // Represents "Expired" 
    { label: "Recycled Claim Standard", value: "RCS" }, // Represents "Suspended"
    { label: "Responsible Down Standard", value: "RDS" }, // Represents "Withdrawn"
    { label: "Responsible Wool Standard", value: "RWS" }, // Represents "Withdrawn"
    { label: "Responsible Mohair Standard", value: "RMS" }, // Represents "Withdrawn"
    { label: "Responsible Alpaca Standard", value: "RAS" }, // Represents "Withdrawn"
    { label: "Content Claim Standard", value: "CCS" }, // Represents "Withdrawn"
  ]

  let normalizedScNo = scopeCertificateDetails.scopeCertificateNo || "";
  if (normalizedScNo.toLowerCase().startsWith("sc-")) {
    normalizedScNo = "SC-" + normalizedScNo.slice(3);
  }

  let normalizedScVersionNo = scopeCertificateDetails.scVersionNo ? scopeCertificateDetails.scVersionNo.toString() : "";
  if (normalizedScVersionNo.toLowerCase().startsWith("sc-")) {
    normalizedScVersionNo = "SC-" + normalizedScVersionNo.slice(3);
  }

  return {
    scId: SC_ID,
    scNo: `${shortCode}-${normalizedScNo}`,
    scVersionNo: `${shortCode}-${normalizedScVersionNo}`, // Convert number to string
    gotsScNo: scopeCertificateDetails.gotsSCNo,
    brandName: scopeCertificateDetails.brandName,
    scCbAuditorId: scopeCertificateDetails.scCBAuditorId,
    scLegacyNo: typeof scopeCertificateDetails.scLegacyNo === 'string'
      ? scopeCertificateDetails.scLegacyNo
      : Array.isArray(scopeCertificateDetails.scLegacyNo)
        ? (scopeCertificateDetails.scLegacyNo as string[]).join(', ')
        : '',
    scNumberOfFarms: scopeCertificateDetails.scNumberOfFarms, // Moved from StandardDetails
    isFarmSC: scopeCertificateDetails.isFarmSC || "", // Persist "Y"/"N" farm flag
    ccsFlag: scopeCertificateDetails.ccsFlag,
    stdClaimsPolicy:scopeCertificateDetails?.stdClaimsPolicy,
    certifiedOrganizationApprovedClaims: scopeCertificateDetails.certifiedOrganizationApprovedClaims,
    scCbLicensingCode: cbLicenseCode,
    scStatus: scopeCertificateDates.scStatus,
    scStatusDescription: scopeCertificateDates.scStatusDescription,
    scSuspensionReason: scopeCertificateDates.scSuspensionReason,
    scSuspensionReasonDescription: scopeCertificateDates.scSuspensionReasonDescription,
    scCreatedDate: scopeCertificateDates.scCreatedDate || "",
    scLastUpdatedDate: scopeCertificateDates.scLastUpdatedDate || "", // Updated to match API field name
    scExtendedUntilDate: scopeCertificateDates.scExtendedUntilDate || "", // Updated to match API field name
    scLastAuditStartDate: scopeCertificateDates.scLastAuditStartDate || "", // Updated to match API field name
    scValidFromDate: scopeCertificateDates.scValidFromDate || "", // Updated to match API field name
    scValidUntilDate: scopeCertificateDates.scValidUntilDate || "", // Updated to match API field name
    scWithdrawalDate: scopeCertificateDates.scWithdrawalDate || "",
    scLastAuditDate: scopeCertificateDates.scLastAuditDate || "",
    scPlaceOfIssue: placeOfIssue,
    scPlaceOfIssueDescription: placeOfIssueDescription,
    scCbAccreditationBody: scopeCertificateDates.scCbAccreditationBody || "",
    scCbAccreditationBodyDescription: scopeCertificateDates.scCbAccreditationBodyDescription || "", // Description for scCbAccreditationBody dropdown
    scCbScopeStipulations: scopeCertificateDates.scCbScopeStipulations || "",
    scCbAccreditationNo: scopeCertificateDates.scCbAccreditationNo || "",
    scCreationTimestamp: scopeCertificateDetails.scCreationTimestamp || new Date().toISOString(), // Add missing field from API
    scProductLimitations: scopeCertificateDetails.scProductLimitations || "", // Add missing field from API
    // Removed: scCertifiedOrganizationApprovedClaims - moved back to StandardDetails
    scStandardInformation: standardDetails.length > 0 ? standardDetails.map((item: any) => {
      const standardCode = item.scStandard ? extractStandardCode(item.scStandard) : "";

      return {
        scStandard: standardCode,
        scStandardDescription: item.scStandardDescription || "",
        scStandardVersion: item.scStandardVersion || "",
        scStandardAddedDate: item.scStandardAddedDate || "",  // API field name
        scStandardRemovedDate: item.scStandardRemovedDate || "",  // API field name
        scStandardWithdrawalReason: Array.isArray(item.scStandardWithdrawalReason) 
          ? item.scStandardWithdrawalReason
          : [],  // API field name - withdrawal reason codes as-is
        scStandardWithdrawalReasonDescription: item.scStandardWithdrawalReasonDescription || "", // Description for scStandardWithdrawalReason dropdown
        scStandardReAdded: item.scStandardReAdded ?? false, // Re-added flag
        certifiedOrganizationApprovedClaims: scopeCertificateDetails.certifiedOrganizationApprovedClaims || "",
      };
    }) : [],
    scNonMandatoryCriterionInformation: scNonMandatoryCriterionInformation.length > 0 ? scNonMandatoryCriterionInformation.map((item: any) => ({
      scNonMandatoryCriterionStandard: item.scNonMandatoryCriterionStandard || "",
      scNonMandatoryCriterionStandardDescription: item.scNonMandatoryCriterionStandardDescription || "", // Description for scNonMandatoryCriterionStandard dropdown
      scNonMandatoryCriterionVersion: item.scNonMandatoryCriterionVersion || "",
      scNonMandatoryCriterionNumber: item.scNonMandatoryCriterionNumber || "",
      scNonMandatoryCriterionStatus: item.scNonMandatoryCriterionStatus || "",
      scNonMandatoryCriterionStatusDescription: item.scNonMandatoryCriterionStatusDescription || "", // Description for scNonMandatoryCriterionStatus dropdown
      scNonMandatoryCriterionDescription: item.scNonMandatoryCriterionDescription || "",
      scNonMandatoryCriterionType: item.scNonMandatoryCriterionType || "",
    })) : [],
    scCbLicensingCodeDescription: scopeCertificateDetails.scCbLicensingCodeDescription || "", // Add missing field from API
    submittedFromUi: (() => {
      const val = scopeCertificateDetails.submittedFromUi;
      if (val === true || val === "Yes" || val === "yes") return "Yes";
      if (val === false || val === "No" || val === "no") return "No";
      return "Yes"; // Default to UI submission since it's saved/submitted from the UI
    })(),
  };
}
